/* eslint-disable react/no-unused-state */

import { withOptimizely } from '@optimizely/react-sdk'; // eslint-disable-line
import { Modal, UI, Utils } from 'common';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import AdaptiveCTA from './AdaptiveCTA.component';
import AddressVerification from './AddressVerification.component';
import { CancelAccountContext } from './CancelAccount.context';
import {
	getCancelQuestionsDropdown1Reason,
	simplifyReason,
} from './CancelAccount.enum';
import CancellationQuestions from './CancellationQuestions.component';
import ClickToCall from './ClickToCall.component';
import ClickToCallConfirmation from './ClickToCallConfirmation.component';
import Downgrade from './Downgrade.component';
import NSACancellation from './NSACancellation.component';

const { ModalContext } = Modal;
const { Logger, Session } = Utils;

export class CancelAccountMain extends Component {
	constructor(props) {
		super(props);

		const { optimizely } = this.props;

		this.state = {
			mask: true,
			step: null,
			cancelAccountInfo: null,
			saveCancelAccountInfo: this.saveCancelAccountInfo.bind(this),
			setStep: this.setStep.bind(this),
			setClickToCallValues: this.setClickToCallValues.bind(this),
			Dropdown1Answer: '',
			Dropdown1Other: '',
			Dropdown2Answer: '',
			Dropdown2Other: '',
			Dropdown3Answer: '',
			Dropdown3Other: '',
			HasViewedDowngrade: null,
			AcceptedDowngrade: null,
			Canceled: null,
			ClickToCallPhoneNumber: '',
			optimizely,
			offeredPlanId: null,
		};
	}

	async componentDidMount() {
		// Do this first to avoid rendering partial cancel account page for downgrade offer users.
		if (
			localStorage.getItem('hasAcceptedBasicOffer') === 'true' ||
			localStorage.getItem('hasAcceptedPaygoOffer') === 'true' ||
			localStorage.getItem('hasAcceptedPaygoOffer2') === 'true' ||
			localStorage.getItem('hasAcceptedRetentionBasicOffer') === 'true' ||
			localStorage.getItem('hasAcceptedRetentionLiteOffer') === 'true'
		) {
			this.setState({
				step: 'Downgrade',
				mask: false,
			});
			return;
		}

		const { showError } = this.context;

		// Collect retention data from localStorage. If it exists, we are coming from the Brightback flow.
		// This is above other variable declarations to speed up the flow.
		const retentionData = JSON.parse(localStorage.getItem('retentionData'));
		if (retentionData) {
			const stateUpdate = {};
			const removeRetentionData = () => {
				localStorage.removeItem('retentionData');
				localStorage.removeItem('hasCancelledViaBrightback');
				localStorage.removeItem('hasAcceptedBasicOffer');
				localStorage.removeItem('hasAcceptedPaygoOffer');
				localStorage.removeItem('hasAcceptedPaygoOffer2');
				localStorage.removeItem('hasAcceptedRetentionBasicOffer');
				localStorage.removeItem('hasAcceptedRetentionLiteOffer');
			};

			const reason = retentionData.reason
				? simplifyReason(retentionData.reason)
				: 'none';

			retentionData.competition = retentionData.competition
				? retentionData.competition.toLowerCase()
				: 'none';

			if (retentionData.competition === 'Other (Specify)') {
				retentionData.competition = 'other';
			}

			stateUpdate.Dropdown1Answer = getCancelQuestionsDropdown1Reason(reason);

			if (reason === 'other') {
				stateUpdate.Dropdown1Other = retentionData.reason_other;
			}

			if (
				['shipstation', 'shipworks', 'other'].includes(
					retentionData.competition
				)
			) {
				stateUpdate.Dropdown3Answer = retentionData.competition;
				stateUpdate.Dropdown2Answer = 'multiCarrier';
			} else if (
				['amazon', 'ebay', 'etsy', 'marketplaces', 'other'].includes(
					retentionData.competition
				)
			) {
				if (retentionData.competition === 'multiple marketplaces') {
					retentionData.competition = 'marketplaces';
				}
				stateUpdate.Dropdown4Answer = retentionData.competition;
				stateUpdate.Dropdown2Answer = 'marketplace';
			}

			this.setState(stateUpdate);

			if (localStorage.getItem('hasCancelledViaBrightback') === 'true') {
				this.setState({
					step: 'AddressVerification',
					mask: false,
				});

				removeRetentionData();
				return;
			}
		}

		const cancelAccountInfo = await AccountCommunication.getCancelAccountInfo();
		const rateSetType = Session.getRateSetType();

		if (!AccountCommunication.isSuccessful(cancelAccountInfo)) {
			this.setState({
				mask: false,
			});
			return showError({ response: cancelAccountInfo });
		}

		if (cancelAccountInfo.data.ClickToCall === 'Eligible') {
			//--------------------
			// perform click-to-call
			this.setState({
				cancelAccountInfo: cancelAccountInfo.data,
				mask: false,
				HasViewedDowngrade: cancelAccountInfo.data.HasViewedDowngrade,
				step: 'ClickToCall',
			});
		} else {
			//--------------------
			// perform splunk logging
			if (cancelAccountInfo.data.ClickToCall === 'Ineligible') {
				Logger.logToServer({
					category: 'cancel-c2c',
					action: 'user-ineligible',
					reason: 'not-eligible',
				});
			} else if (cancelAccountInfo.data.ClickToCall === 'Disabled') {
				Logger.logToServer({
					category: 'cancel-c2c',
					action: 'user-ineligible',
					reason: 'switch-disabled',
				});
			} else if (
				cancelAccountInfo.data.ClickToCall === 'CustomerCareNotAvailable'
			) {
				Logger.logToServer({
					category: 'cancel-c2c',
					action: 'user-ineligible',
					reason: 'customer-care-not-available',
				});
			}

			//--------------------
			// resume original cancel account
			this.setState({
				cancelAccountInfo: cancelAccountInfo.data,
				mask: false,
				HasViewedDowngrade: cancelAccountInfo.data.HasViewedDowngrade,
				step:
					rateSetType === 'NSA' || rateSetType === 'CPP'
						? 'NSACancellation'
						: 'CancellationQuestions',
			});
		}
	}

	setStep(val) {
		this.setState({
			step: val,
		});
	}

	setClickToCallValues(phoneNumber) {
		this.setState({
			ClickToCallPhoneNumber: phoneNumber,
		});
	}

	async saveCancelAccountInfo(data, callback) {
		Logger.saveActionData('Cancel Account Info Saved');

		const response = await AccountCommunication.saveCancelAccountInfo(data);
		const { showError } = this.context;

		if (!AccountCommunication.isSuccessful(response)) {
			return showError({ response });
		}

		this.setState(data, () => {
			callback && callback(); // eslint-disable-line no-unused-expressions
		});
	}

	render() {
		const { step, mask } = this.state;

		if (mask) {
			return <UI.Spinner />;
		} else {
			return (
				<div className="account-cancel-account-container">
					<CancelAccountContext.Provider value={this.state}>
						{step === 'NSACancellation' && <NSACancellation />}
						{step === 'CancellationQuestions' && <CancellationQuestions />}
						{step === 'AdaptiveCTA' && <AdaptiveCTA />}
						{step === 'Downgrade' && <Downgrade {...this.props} />}
						{step === 'AddressVerification' && (
							<AddressVerification {...this.props} />
						)}
						{step === 'ClickToCall' && <ClickToCall />}
						{step === 'ClickToCallConfirmation' && <ClickToCallConfirmation />}
					</CancelAccountContext.Provider>
				</div>
			);
		}
	}
}

CancelAccountMain.contextType = ModalContext;

export const CancelAccount = withOptimizely(CancelAccountMain);
